import moment from "moment";
import { z } from "zod";

import type { TFunction } from "i18next";

import type {
  FreecopyCreate,
  FreecopyUpdate,
  GetFreecopyResponse,
} from "@/src/api/OfferAPI.types";

export const FREECOPY_ACTIVATED_STATUS = "activated";
export const DEFAULT_DAYS_DURATION = 7;

export interface FreecopyFormValues {
  email: string;
  lang: string;
  offerPlanUuid: string;
  daysDuration: string;
  activationLimitDate: string;
  subscriptionEndDate: string;
}

interface BuildFreecopyFormSchemaParams {
  t: TFunction;
  isActivated: boolean;
  subscriptionBeginDate?: string;
}

export function isFreecopyActivated(freecopy?: GetFreecopyResponse): boolean {
  return freecopy?.status === FREECOPY_ACTIVATED_STATUS;
}

function isPositiveInteger(value: string): boolean {
  const number = Number(value);
  return value !== "" && Number.isInteger(number) && number >= 1;
}

export function buildFreecopyFormSchema({
  t,
  isActivated,
  subscriptionBeginDate,
}: BuildFreecopyFormSchemaParams) {
  const baseSchema = z.object({
    email: z.string(),
    lang: z.string(),
    offerPlanUuid: z.string().min(1, t("editor-errors:offerNull")),
    daysDuration: z.string(),
    activationLimitDate: z.string(),
    subscriptionEndDate: z.string(),
  });

  if (isActivated) {
    return baseSchema.extend({
      subscriptionEndDate: z
        .string()
        .min(1, t("editor-errors:subscriptionEndDateNull"))
        .refine(
          (date) =>
            !subscriptionBeginDate ||
            !moment(date).isBefore(moment(subscriptionBeginDate)),
          t("editor-errors:subscriptionEndMustBeInFuture"),
        ),
    });
  }

  return baseSchema.extend({
    email: z
      .string()
      .trim()
      .min(1, t("editor-errors:emailNull"))
      .pipe(z.email(t("editor-errors:emailInvalid"))),
    daysDuration: z
      .string()
      .refine(isPositiveInteger, t("editor-errors:durationDaysNull")),
    activationLimitDate: z
      .string()
      .min(1, t("editor-errors:limiteDateNull"))
      .refine(
        (date) => !moment(date).isBefore(moment()),
        t("editor-errors:limiteMustBeInFuture"),
      ),
  });
}

export function getFreecopyFormDefaultValues(
  freecopy: GetFreecopyResponse | undefined,
  defaultLang: string,
): FreecopyFormValues {
  return {
    email: freecopy?.email ?? "",
    lang: defaultLang,
    offerPlanUuid: freecopy?.offer_plan?.uuid ?? "",
    daysDuration: String(freecopy?.days_duration ?? DEFAULT_DAYS_DURATION),
    activationLimitDate: freecopy?.activation_limit_date ?? "",
    subscriptionEndDate: freecopy?.subscription?.end_date ?? "",
  };
}

function toActivablePayload(
  values: FreecopyFormValues,
): Omit<FreecopyCreate, "lang"> {
  return {
    offer_plan_uuid: values.offerPlanUuid,
    email: values.email,
    days_duration: Number(values.daysDuration),
    activation_limit_date: values.activationLimitDate,
  };
}

export function toFreecopyCreate(values: FreecopyFormValues): FreecopyCreate {
  return { ...toActivablePayload(values), lang: values.lang };
}

export function toFreecopyUpdate(
  values: FreecopyFormValues,
  freecopy: GetFreecopyResponse,
): FreecopyUpdate {
  if (isFreecopyActivated(freecopy)) {
    return {
      offer_plan_uuid: values.offerPlanUuid,
      subscription_begin_date: freecopy.subscription?.begin_date,
      subscription_end_date: values.subscriptionEndDate,
    };
  }

  return toActivablePayload(values);
}
